import { FC, useMemo } from 'react';
import { Box, Button, Chip, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { TPgClientNotificationSetting } from 'services/api/paymentGateway/types';
import { PgPanel } from '../common/PgPanel';

type TCustomerEmailsPanelProps = {
	/** Values on screen. */
	notifications: TPgClientNotificationSetting[];
	/** Saved values. */
	saved: TPgClientNotificationSetting[];
	disabled?: boolean;
	onToggle: (type: string, enabled: boolean) => void;
	onSetAll: (enabled: boolean) => void;
};

/** Customer e-mails sent by the gateway: one switch per type. */
export const CustomerEmailsPanel: FC<TCustomerEmailsPanelProps> = ({
	notifications,
	saved,
	disabled,
	onToggle,
	onSetAll,
}) => {
	const savedEnabled = useMemo(() => new Map(saved.map((n) => [n.type, n.enabled])), [saved]);
	const enabledCount = notifications.filter((n) => n.enabled).length;
	const changed = notifications.some((n) => savedEnabled.get(n.type) !== n.enabled);

	return (
		<PgPanel
			title={
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
					<Typography color="primary" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
						Customer e-mails
					</Typography>
					{changed && <Chip size="small" label="Unsaved" color="warning" variant="outlined" />}
					<Chip
						size="small"
						variant="outlined"
						label={`${enabledCount} of ${notifications.length} enabled`}
						color={enabledCount === notifications.length ? 'success' : 'default'}
					/>
				</Box>
			}
			actions={
				<>
					<Button
						size="small"
						onClick={() => onSetAll(true)}
						disabled={disabled || enabledCount === notifications.length}
					>
						Enable all
					</Button>
					<Button
						size="small"
						onClick={() => onSetAll(false)}
						disabled={disabled || enabledCount === 0}
					>
						Disable all
					</Button>
				</>
			}
		>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				E-mails the gateway sends to customers. A disabled e-mail is not sent, and it is not sent
				later either when the e-mail is enabled again.
			</Typography>
			{notifications.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No e-mail types.
				</Typography>
			) : (
				<Grid container spacing={1}>
					{notifications.map((n) => (
						<Grid item xs={12} md={6} lg={4} key={n.type}>
							<FormControlLabel
								sx={{ alignItems: 'flex-start', m: 0 }}
								control={
									<Switch
										checked={n.enabled}
										onChange={(e) => onToggle(n.type, e.target.checked)}
										disabled={disabled}
									/>
								}
								label={
									<Box sx={{ pt: '6px' }}>
										<Typography
											variant="body2"
											color={n.enabled ? 'text.primary' : 'text.secondary'}
										>
											{n.label}
										</Typography>
										<Typography variant="caption" color="text.secondary" component="div">
											{n.type}
										</Typography>
									</Box>
								}
							/>
						</Grid>
					))}
				</Grid>
			)}
		</PgPanel>
	);
};
